/**
 * Tells a dropped connection apart from a sync failure worth reporting.
 *
 * The sync stream is a long-lived HTTP request, so it dies for entirely routine reasons: iOS tears
 * down sockets when the app is backgrounded, and a phone moving between cells or on and off Wi-Fi
 * does the same. PowerSync's own retry loop recovers from all of these unaided — see the note in
 * `connectVerified` — so reporting them as issues only buries the failures that do need a human:
 * bad credentials, a rejected upload, a server answering with an error.
 */

/**
 * Matched against the error message. These strings come from the platform networking stacks
 * (CFNetwork on iOS, OkHttp on Android, the fetch polyfill on web) rather than from PowerSync, and
 * none of them carry a stable code, so matching on text is the only option.
 */
const TRANSIENT_MESSAGE_PATTERNS = [
  /network connection was lost/i,
  /software caused connection abort/i,
  /network request failed/i,
  /request timed out/i,
  /could not connect to the server/i,
  /internet connection appears to be offline/i,
  /connection (?:closed|aborted|refused|reset)/i,
  /connection reset by peer/i,
  /broken pipe/i,
  /socket hang up/i,
  /stream (?:was )?closed/i,
  /unexpected end of stream/i,
  // Chromium and WebKit respectively, for a fetch that never got a response.
  /failed to fetch/i,
  /load failed/i,
  // Chromium, for a response body that stopped mid-stream (sleep, a network change), and axios's
  // `ERR_NETWORK` message. Anchored, so it cannot swallow a longer message that merely mentions it.
  /^network error$/i,
  // Firefox's wording for the same two failures: a fetch that never got a response, and a response
  // body that stopped mid-stream. The second is anchored for the same reason as above.
  /networkerror when attempting to fetch resource/i,
  /^error in input stream$/i,
];

/** Node- and axios-style codes for the same class of failure. */
const TRANSIENT_CODES = new Set([
  'ECONNABORTED',
  'ECONNREFUSED',
  'ECONNRESET',
  'EAI_AGAIN',
  'ENETDOWN',
  'ENETUNREACH',
  'ENOTFOUND',
  'EPIPE',
  'ETIMEDOUT',
  'ERR_NETWORK',
]);

/** The fetch polyfill re-wraps the platform failure, so the real message can sit a few levels down. */
const MAX_CAUSE_DEPTH = 3;

type ErrorLike = {
  message?: unknown;
  code?: unknown;
  name?: unknown;
  cause?: unknown;
  response?: { status?: unknown };
};

const hasTransientMessage = (value: unknown): boolean =>
  typeof value === 'string' && TRANSIENT_MESSAGE_PATTERNS.some((pattern) => pattern.test(value));

const check = (error: unknown, depth: number): boolean => {
  if (typeof error === 'string') {
    return hasTransientMessage(error);
  }

  if (typeof error !== 'object' || error === null) {
    return false;
  }

  const candidate = error as ErrorLike;

  // A status means the server answered, so whatever went wrong is not a dropped connection — a 401
  // from an expired token and a 5xx both belong in front of a human.
  if (typeof candidate.response?.status === 'number') {
    return false;
  }

  if (typeof candidate.code === 'string' && TRANSIENT_CODES.has(candidate.code)) {
    return true;
  }

  // What `AbortController` throws when the platform cancels an in-flight request, which is how a
  // backgrounded app's sync stream usually ends.
  if (candidate.name === 'AbortError') {
    return true;
  }

  if (hasTransientMessage(candidate.message)) {
    return true;
  }

  if (depth < MAX_CAUSE_DEPTH && candidate.cause !== undefined) {
    return check(candidate.cause, depth + 1);
  }

  return false;
};

export const isTransientNetworkError = (error: unknown): boolean => check(error, 0);
